import { useEffect, useMemo, useState } from 'react'
import {
  attendanceCreateLeader,
  attendanceDeactivateLeader,
  attendanceLoadLeaderAssignments,
  attendanceLoadLeaderSubordinates,
  attendanceLoadLeaders,
  attendanceSetLeaderSubordinates,
  loadSchoolEmployees,
  type AttendanceLeader,
  type AttendanceLeaderAssignment,
  type AttendanceLeaderSubordinate,
} from '../../lib/apiPhpAdapter'
import type { User } from '../../types'
import { formatEmployeeFullName } from '../../utils/employeeDisplay'
import { comparePersonNames } from '../../utils/personNameSort'
import './AttendanceOrgAdmin.css'

function employeeSchoolId(e: User): number | null {
  if (e.school_employee_id != null) return Number(e.school_employee_id)
  const id = String(e.id || '')
  const m = id.match(/^se-(\d+)$/)
  if (m) return Number(m[1])
  return null
}

function filterBySearch(list: User[], q: string) {
  if (!q) return list
  const needle = q.trim().toLowerCase()
  return list.filter(
    (e) =>
      e.name.toLowerCase().includes(needle) ||
      (e.email || '').toLowerCase().includes(needle) ||
      String(e.personal_number || '')
        .toLowerCase()
        .includes(needle),
  )
}

export function AttendanceLeadersAdminPanel({
  showAlert,
}: {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}) {
  const [leaders, setLeaders] = useState<AttendanceLeader[]>([])
  const [employees, setEmployees] = useState<User[]>([])
  const [assignments, setAssignments] = useState<AttendanceLeaderAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLeaderId, setSelectedLeaderId] = useState<number | null>(null)
  const [subs, setSubs] = useState<AttendanceLeaderSubordinate[]>([])
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<number>>(() => new Set())
  const [addPickerId, setAddPickerId] = useState('')
  const [leaderSearch, setLeaderSearch] = useState('')
  const [availSearch, setAvailSearch] = useState('')
  const [assignedSearch, setAssignedSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  const reloadAssignments = async () => {
    const rows = await attendanceLoadLeaderAssignments()
    setAssignments(rows)
    return rows
  }

  const reloadLeaders = async () => {
    const [list] = await Promise.all([attendanceLoadLeaders(), reloadAssignments()])
    setLeaders(list)
    return list
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [list, empList, asg] = await Promise.all([
          attendanceLoadLeaders(),
          loadSchoolEmployees(),
          attendanceLoadLeaderAssignments(),
        ])
        if (cancelled) return
        setLeaders(list)
        setEmployees(Array.isArray(empList) ? empList : [])
        setAssignments(asg)
        if (list.length > 0) {
          const firstActive = list.find((l) => l.active) || list[0]
          setSelectedLeaderId(firstActive.id)
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst vedoucí', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showAlert])

  useEffect(() => {
    if (selectedLeaderId == null) {
      setSubs([])
      setSelectedEmployeeIds(new Set())
      setDirty(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const rows = await attendanceLoadLeaderSubordinates(selectedLeaderId)
        if (cancelled) return
        setSubs(rows)
        setSelectedEmployeeIds(new Set(rows.map((r) => Number(r.employee_id))))
        setDirty(false)
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst podřízené', 'error')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedLeaderId, showAlert])

  const leaderSchoolIds = useMemo(() => {
    const ids = new Set<number>()
    for (const l of leaders) {
      if (!l.active) continue
      if (l.school_employee_id != null) ids.add(Number(l.school_employee_id))
    }
    return ids
  }, [leaders])

  /** employee_id → leader_id (jiný než právě vybraný) */
  const takenByOtherLeaders = useMemo(() => {
    const map = new Map<number, { leader_id: number; leader_name?: string | null }>()
    for (const a of assignments) {
      const eid = Number(a.employee_id)
      const lid = Number(a.leader_id)
      if (!eid || !lid) continue
      if (selectedLeaderId != null && lid === selectedLeaderId) continue
      map.set(eid, { leader_id: lid, leader_name: a.leader_name })
    }
    return map
  }, [assignments, selectedLeaderId])

  const withPersonalNumber = useMemo(
    () =>
      employees.filter((e) => {
        const sid = employeeSchoolId(e)
        return sid != null && String(e.personal_number || '').trim() !== ''
      }),
    [employees],
  )

  const leaderCandidates = useMemo(() => {
    const list = withPersonalNumber.filter((e) => {
      const sid = employeeSchoolId(e)
      return sid != null && !leaderSchoolIds.has(sid)
    })
    return filterBySearch(list, leaderSearch)
  }, [withPersonalNumber, leaderSchoolIds, leaderSearch])

  const employeesBySid = useMemo(() => {
    const map = new Map<number, User>()
    for (const e of withPersonalNumber) {
      const sid = employeeSchoolId(e)
      if (sid != null) map.set(sid, e)
    }
    return map
  }, [withPersonalNumber])

  const assignedList = useMemo(() => {
    const list: User[] = []
    for (const sid of selectedEmployeeIds) {
      const e = employeesBySid.get(sid)
      if (e) list.push(e)
    }
    list.sort(comparePersonNames)
    return filterBySearch(list, assignedSearch)
  }, [selectedEmployeeIds, employeesBySid, assignedSearch])

  const availableList = useMemo(() => {
    const list = withPersonalNumber.filter((e) => {
      const sid = employeeSchoolId(e)!
      if (selectedEmployeeIds.has(sid)) return false
      // Už podřízený jiného vedoucího — nenabízet
      if (takenByOtherLeaders.has(sid)) return false
      return true
    })
    list.sort(comparePersonNames)
    return filterBySearch(list, availSearch)
  }, [withPersonalNumber, selectedEmployeeIds, takenByOtherLeaders, availSearch])

  const selectedLeader = leaders.find((l) => l.id === selectedLeaderId) || null

  const addToAssigned = (sid: number) => {
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev)
      next.add(sid)
      return next
    })
    setDirty(true)
  }

  const removeFromAssigned = (sid: number) => {
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev)
      next.delete(sid)
      return next
    })
    setDirty(true)
  }

  const selectLeader = (id: number) => {
    if (dirty && !window.confirm('Máte neuložené změny podřízených. Přepnout bez uložení?')) {
      return
    }
    setSelectedLeaderId(id)
  }

  return (
    <section className="attendance-panel att-org">
      <h2>Vedoucí zaměstnanci</h2>
      <p className="attendance-muted">
        1) Přidejte vedoucího ze seznamu zaměstnanců. 2) Vyberte ho vlevo. 3) Přesuňte podřízené zleva
        doprava a uložte. Zaměstnanec už přiřazený jinému vedoucímu se v „Dostupní“ nezobrazuje.
      </p>
      {loading ? <p>Načítání…</p> : null}

      <div className="att-org__grid">
        <div className="att-org__col">
          <h3>Vedoucí</h3>
          <div className="att-org__add">
            <input
              type="search"
              placeholder="Filtrovat kandidáty…"
              value={leaderSearch}
              onChange={(e) => setLeaderSearch(e.target.value)}
            />
            <select value={addPickerId} onChange={(e) => setAddPickerId(e.target.value)}>
              <option value="">— vybrat ({leaderCandidates.length}) —</option>
              {leaderCandidates.map((e) => {
                const sid = employeeSchoolId(e)!
                return (
                  <option key={sid} value={String(e.id)}>
                    {formatEmployeeFullName(e)}
                    {e.personal_number ? ` · os. ${e.personal_number}` : ''}
                  </option>
                )
              })}
            </select>
            <button
              type="button"
              className="att-org__btn att-org__btn--primary"
              disabled={!addPickerId || saving}
              onClick={async () => {
                setSaving(true)
                try {
                  const emp = employees.find((e) => String(e.id) === addPickerId)
                  const sid = emp ? employeeSchoolId(emp) : null
                  const created = await attendanceCreateLeader(
                    sid != null
                      ? { school_employee_id: sid, employee_picker_id: addPickerId }
                      : { employee_picker_id: addPickerId },
                  )
                  await reloadLeaders()
                  setSelectedLeaderId(created.id)
                  setAddPickerId('')
                  setLeaderSearch('')
                  setDirty(false)
                  showAlert('Vedoucí přidán', 'success')
                } catch (e: unknown) {
                  showAlert(e instanceof Error ? e.message : 'Chyba při přidání', 'error')
                } finally {
                  setSaving(false)
                }
              }}
            >
              Přidat vedoucího
            </button>
          </div>

          <ul className="att-org__leaders">
            {leaders.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className={
                    selectedLeaderId === l.id
                      ? 'att-org__leader att-org__leader--active'
                      : 'att-org__leader'
                  }
                  onClick={() => selectLeader(l.id)}
                >
                  <span className="att-org__leader-name">
                    {l.user_name || `Zaměstnanec #${l.school_employee_id ?? l.id}`}
                    {!l.active ? ' (neaktivní)' : ''}
                  </span>
                  <span className="att-org__leader-meta">
                    {l.personal_number ? `os. ${l.personal_number} · ` : ''}
                    podřízených: {l.subordinates_count ?? 0}
                  </span>
                </button>
                {l.active ? (
                  <button
                    type="button"
                    className="att-org__link-danger"
                    onClick={async () => {
                      if (!window.confirm('Deaktivovat tohoto vedoucího?')) return
                      try {
                        await attendanceDeactivateLeader(l.id)
                        const list = await reloadLeaders()
                        if (selectedLeaderId === l.id) {
                          const next = list.find((x) => x.active)
                          setSelectedLeaderId(next?.id ?? null)
                        }
                        showAlert('Vedoucí deaktivován', 'success')
                      } catch (err: unknown) {
                        showAlert(err instanceof Error ? err.message : 'Chyba', 'error')
                      }
                    }}
                  >
                    Deaktivovat
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {leaders.length === 0 && !loading ? (
            <p className="attendance-muted">Zatím žádný vedoucí.</p>
          ) : null}
        </div>

        <div className="att-org__col att-org__col--wide">
          <h3>
            Podřízení
            {selectedLeader
              ? ` — ${selectedLeader.user_name || selectedLeader.user_id || ''}`
              : ''}
          </h3>
          {!selectedLeader ? (
            <p className="attendance-muted">Vyberte vedoucího vlevo.</p>
          ) : (
            <>
              <div className="att-org__dual">
                <div className="att-org__box">
                  <div className="att-org__box-head">
                    <strong>Dostupní</strong>
                    <span>{availableList.length}</span>
                  </div>
                  <input
                    type="search"
                    placeholder="Hledat…"
                    value={availSearch}
                    onChange={(e) => setAvailSearch(e.target.value)}
                  />
                  <ul className="att-org__people">
                    {availableList.map((e) => {
                      const sid = employeeSchoolId(e)!
                      return (
                        <li key={sid}>
                          <button type="button" onClick={() => addToAssigned(sid)}>
                            <span className="att-org__person-name">{formatEmployeeFullName(e)}</span>
                            <span className="att-org__person-meta">
                              {e.personal_number ? `os. ${e.personal_number}` : '—'}
                              {e.email ? ` · ${e.email}` : ''}
                            </span>
                            <span className="att-org__add-mark">+</span>
                          </button>
                        </li>
                      )
                    })}
                    {availableList.length === 0 ? (
                      <li className="att-org__empty">
                        {takenByOtherLeaders.size > 0
                          ? 'Nikdo k přidání (ostatní už mají vedoucího)'
                          : 'Nikdo k přidání'}
                      </li>
                    ) : null}
                  </ul>
                </div>

                <div className="att-org__box att-org__box--assigned">
                  <div className="att-org__box-head">
                    <strong>Podřízení vedoucího</strong>
                    <span>{selectedEmployeeIds.size}</span>
                  </div>
                  <input
                    type="search"
                    placeholder="Hledat v přiřazených…"
                    value={assignedSearch}
                    onChange={(e) => setAssignedSearch(e.target.value)}
                  />
                  <ul className="att-org__people">
                    {assignedList.map((e) => {
                      const sid = employeeSchoolId(e)!
                      return (
                        <li key={sid}>
                          <button type="button" onClick={() => removeFromAssigned(sid)}>
                            <span className="att-org__person-name">{formatEmployeeFullName(e)}</span>
                            <span className="att-org__person-meta">
                              {e.personal_number ? `os. ${e.personal_number}` : '—'}
                              {e.email ? ` · ${e.email}` : ''}
                            </span>
                            <span className="att-org__remove-mark">×</span>
                          </button>
                        </li>
                      )
                    })}
                    {assignedList.length === 0 ? (
                      <li className="att-org__empty">Zatím bez podřízených</li>
                    ) : null}
                  </ul>
                </div>
              </div>

              <div className="att-org__save-row">
                <span className="attendance-muted">
                  {dirty
                    ? 'Neuložené změny'
                    : subs.length > 0
                      ? `Uloženo: ${subs.length} podřízených`
                      : 'Žádní podřízení'}
                </span>
                <button
                  type="button"
                  className="att-org__btn att-org__btn--primary"
                  disabled={saving || !dirty}
                  onClick={async () => {
                    if (selectedLeaderId == null) return
                    setSaving(true)
                    try {
                      await attendanceSetLeaderSubordinates(selectedLeaderId, [
                        ...selectedEmployeeIds,
                      ])
                      const rows = await attendanceLoadLeaderSubordinates(selectedLeaderId)
                      setSubs(rows)
                      await reloadLeaders()
                      await reloadAssignments()
                      setDirty(false)
                      showAlert('Podřízení uloženi', 'success')
                    } catch (e: unknown) {
                      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
                    } finally {
                      setSaving(false)
                    }
                  }}
                >
                  {saving ? 'Ukládám…' : 'Uložit podřízené'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
